<template>
  <div class="radar">
    <transition name="fade">
      <screen-loading v-if="!mapCenter" />
      <router-view
        ref="gmap"
        v-else
        @refreshFilters="refreshFilters($route, true)"
        @refreshCountries="refreshCountries"
        @refreshLocations="refreshLocations"
        @refreshAnalytics="refreshAnalytics"
        @refreshAlgoliaFilters="refreshAlgoliaFilters"
        :previous-route="previousRoute"
        :next-route="nextRoute"
      ></router-view>
    </transition>
    <transition name="fade">
      <screen-refreshing v-if="mapCenter && (refreshingData || refreshingZones)" />
    </transition>
    <views v-if="mapCenter && !noUi" :settings="computedSettings" />
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import {
  buildFilters,
  buildSource,
  buildDate,
  buildBrands,
  buildDistribution,
  buildLocation,
  buildDivision,
  buildLevel,
  buildLocationMode,
  buildCountries,
  buildView,
  buildGroups,
} from '@/utils/urlParams.util'
import { buildAlgoliaFacets } from '@/utils/algolia.util'

import ScreenLoading from '@/components/Screen/Loading.vue'
import ScreenRefreshing from '@/components/Screen/Refreshing.vue'
import Views from '@/components/Views.vue'

export default {
  name: 'Radar',
  components: {
    ScreenLoading,
    ScreenRefreshing,
    Views,
  },
  data() {
    return {
      previousRoute: null,
      nextRoute: null,
    }
  },
  mounted() {
    this.initView(this.$route)

    const locale = this.$route.query.locale
    if (locale) {
      this.$i18n.locale = locale
      this.updateLocale(locale)
    }
  },
  computed: {
    ...mapState({
      settings: state => state.data.settings,
      sources: state => state.data.sources,
      brands: state => state.data.brands,
      filters: state => state.data.filters,
      locations: state => state.data.locations,
      distributions: state => state.data.distributions,
      subDivisions: state => state.data.subDivisions,
      saveGeoJson: state => state.data.saveGeoJson,

      currentKpi: state => state.refine.currentKpi,
      currentSource: state => state.refine.currentSource,
      currentDistribution: state => state.refine.currentDistribution,
      currentDistance: state => state.refine.currentDistance,
      currentCountries: state => state.refine.currentCountries,
      currentTimeline: state => state.refine.currentTimeline,
      activeBrands: state => state.refine.activeBrands,
      currentSectors: state => state.refine.currentSectors,
      activeGroups: state => state.refine.activeGroups,
      activeFilters: state => state.refine.activeFilters,

      groupTabIsActive: state => state.radar.filters.group,
      mapCenter: state => state.radar.map.center,
      currentMapCenter: state => state.radar.currentMapCenter,
      currentMapRadius: state => state.radar.currentMapRadius,
      mapBounds: state => state.radar.map.bounds,
      map: state => state.radar.map,
      refreshingData: state => state.radar.refreshing.data,
      refreshingZones: state => state.radar.refreshing.zones,
      noUi: state => state.radar.noUi,
      radarFilters: state => state.radar.filters,

      markersIndex: state => state.algolia.markersIndex,
      circlesIndex: state => state.algolia.circlesIndex,
      nbDisplayCircle: state => state.algolia.settings.circle.nbMaxDisplay,
      nbDisplayMarker: state => state.algolia.settings.marker.nbMaxDisplay,
    }),
    ...mapGetters({
      isZonesIntentionView: 'radar/isZonesIntentionView',
      indexCircleOrCountiesByZoom: 'algolia/indexCircleOrCountiesByZoom',
    }),
    computedSettings() {
      return this.settings.filter(setting => setting.key === this.currentKpi)[0]
    },
    box() {
      return [
        this.mapBounds.getSouthWest().lat(),
        this.mapBounds.getSouthWest().lng(),
        this.mapBounds.getNorthEast().lat(),
        this.mapBounds.getNorthEast().lng(),
      ]
    },
  },
  methods: {
    ...mapActions({
      setClientId: 'data/setClientId',
      setGeoJsonSave: 'data/setGeoJsonSave',
      getClientSettings: 'data/getClientSettings',
      getClientBrands: 'data/getClientBrands',
      getClientFilters: 'data/getClientFilters',
      getClientSearch: 'data/getClientSearch',
      getClientSources: 'data/getClientSources',
      getClientDistributions: 'data/getClientDistributions',
      getClientLocations: 'data/getClientLocations',
      getReturnZones: 'data/getReturnZones',
      getClientSubDivisions: 'data/getClientSubDivisions',
      getClientAnalytics: 'data/getClientAnalytics',
      setCurrentLocation: 'data/setCurrentLocation',
      setZones: 'data/setZones',

      initCurrentKpi: 'refine/initCurrentKpi',
      initCurrentSource: 'refine/initCurrentSource',
      resetCurrentSource: 'refine/resetCurrentSource',
      initCurrentDistribution: 'refine/initCurrentDistribution',
      initCurrentCountries: 'refine/initCurrentCountries',
      initActiveBrands: 'refine/initActiveBrands',
      initActiveFilters: 'refine/initActiveFilters',
      updateCurrentDistance: 'refine/updateCurrentDistance',
      updateActiveBrands: 'refine/updateActiveBrands',
      updateCurrentSectors: 'refine/updateCurrentSectors',

      initMapCenter: 'radar/initMapCenter',
      initSettings: 'radar/initSettings',
      updateMapCenterFromLocations: 'radar/updateMapCenterFromLocations',
      updateLocale: 'radar/updateLocale',
      updateStackButton: 'radar/updateStackButton',
      toggleRefreshData: 'radar/toggleRefreshData',

      initMarkersIndex: 'algolia/initMarkersIndex',
      initCirclesIndex: 'algolia/initCirclesIndex',
      initCountiesIndex: 'algolia/initCountiesIndex',
      search: 'algolia/search',
      searchMultipleQueries: 'algolia/searchMultipleQueries',
    }),
    async initView(route) {
      const query = route.query
      const view = query.view
      let urlParams = buildView(view)
      this.setClientId(route.params.id)
      let settings
      settings = await this.getClientSettings(urlParams)
      if (settings) {
        this.getClientSubDivisions()
          .then(() => {
            this.initCurrentKpi({ settings: this.settings, query })

            const c = this.subDivisions[this.currentKpi] ? this.subDivisions[this.currentKpi].subdivisions : []
            const s = this.settings.find(setting => setting.key === this.currentKpi)
            this.initCurrentCountries({ countries: c, setting: s, query })
            return this.refreshCountries(false, this.$route, true)
          })
          .then(() => {
            this.initMapCenter(this.locations[this.currentKpi])
          })
          .catch(e => {
            this.$router.push({ name: 'error-default', params: { error: e } })
          })
      }
    },
    refreshCountries(mapBounds = false, route = this.$route, isInit = false) {
      this.toggleRefreshData(true)
      const query = route.query
      const level = route.query.level || route.params.level || 1
      const divisionLevel = route.params.level
      const divisionKey = route.params.key
      let urlParams = ''

      urlParams += buildLevel(level)
      urlParams += buildCountries(this.currentCountries)
      urlParams += buildDivision(divisionLevel, divisionKey)
      const filtersParams = urlParams + buildLocationMode(route.name, true)

      urlParams += buildLocationMode(route.name)
      if (mapBounds) {
        this.nextRoute = { name: 'markers' }
      }

      const Promises = [
        this.getClientBrands({ route, urlParams }),
        this.getClientFilters(filtersParams),
        this.initSettings(query),
      ]
      if (route.name !== 'divisionZonesIntention') Promises.push(this.getClientSearch(urlParams))
      return Promise.all(Promises)
        .then(() => {
          const b = this.brands[this.currentKpi] ? this.brands[this.currentKpi].brands : []
          const f = this.filters[this.currentKpi] ? this.filters[this.currentKpi].filters : []
          const c = this.subDivisions[this.currentKpi] ? this.subDivisions[this.currentKpi].subdivisions : []

          this.initActiveBrands({ brands: b, query })
          this.initActiveFilters({ filters: f, query })
          this.updateCurrentDistance(query.distance)
          this.updateStackButton({ currCountries: this.currentCountries, countries: c })
        })
        .then(() => {
          if (this.currentSectors.length > 0) {
            const sectors = this.computedSettings?.sectors.filter(s => this.currentSectors.includes(s.name))
            let key = sectors.map(sector => sector.admin_codes).reduce((a, b) => `${a},${b}`, [])
            if (key[0] === ',') {
              key = key.replace(',', '')
            }
            this.$router.push({
              name: this.$route.name === 'markers' ? 'division' : 'divisionZones',
              params: {
                ...this.$route.params,
                level: sectors[0].level || 2,
                key,
              },
              query: this.$route.query,
            })
            this.updateCurrentSectors([])
            return Promise.resolve()
          }
          return this.refreshFilters(route, false, isInit)
        })
    },
    refreshFilters(route, reset = false, isInit) {
      this.toggleRefreshData(true)
      this.setCurrentLocation(null)
      const query = route.query
      const level = route.query.level || route.params.level || 1
      const divisionLevel = route.params.level
      const divisionKey = route.params.key
      const setting = this.settings.filter(setting => setting.key === this.currentKpi)[0]

      let urlParams = ''
      urlParams += buildGroups(this.activeGroups)
      urlParams += buildBrands(this.activeBrands, this.brands[this.currentKpi].brands, this.activeGroups)
      urlParams += buildFilters(this.activeFilters)
      urlParams += buildCountries(this.currentCountries)
      urlParams += buildDate(this.currentTimeline)
      urlParams += buildLocationMode(route.name)
      urlParams += buildDivision(divisionLevel, divisionKey)
      urlParams += buildLevel(level)

      return this.getClientSources({ setting, urlParams })
        .then(() => {
          const s = this.sources[this.currentKpi] ? this.sources[this.currentKpi].sources : []
          if (reset) {
            return this.resetCurrentSource(s)
          } else {
            return this.initCurrentSource({ sources: s, query })
          }
        })
        .then(() => {
          urlParams += buildSource(this.currentSource)
          return this.getClientDistributions({ setting, urlParams })
        })
        .then(() => {
          const d = this.distributions[this.currentKpi]
          if (reset) {
            return this.initCurrentDistribution({ distribution: d })
          } else {
            return this.initCurrentDistribution({ distribution: d, query })
          }
        })
        .then(() => {
          if (route.name !== 'analytics') {
            return this.refreshLocations()
          } else {
            return Promise.all([this.refreshLocations(), this.refreshAnalytics()])
          }
        })
        .then(() => {
          if (isInit) this.updateMapCenterFromLocations(this.locations[this.currentKpi])
        })
    },
    refreshLocations(closeCurrentLocation = true) {
      const routeWithLevels = ['zones', 'zonesIntention', 'divisionZonesIntention', 'divisionZones', 'division']
      const route = this.$route
      this.toggleRefreshData(true)
      if (closeCurrentLocation) {
        this.setCurrentLocation(null)
      }

      const internalId = route.params.location
      const divisionLevel = route.params.level
      const divisionKey = route.params.key
      const level = route.query.level || route.params.level || (this.isZonesIntentionView ? 1 : 2)
      const setting = this.settings.filter(setting => setting.key === this.currentKpi)[0]
      let urlParams = ''
      urlParams += buildSource(this.currentSource)
      urlParams += buildDistribution(this.currentDistribution, this.radarFilters.distribution)
      urlParams += buildGroups(this.activeGroups)
      urlParams += buildBrands(this.activeBrands, this.brands[this.currentKpi].brands, this.activeGroups)
      urlParams += buildFilters(this.activeFilters)
      urlParams += buildLocation(internalId, this.currentDistance)
      urlParams += buildDivision(divisionLevel, divisionKey)
      urlParams += buildDate(this.currentTimeline)
      urlParams += buildLocationMode(route.name)

      return Promise.all([
        routeWithLevels.includes(this.$route.name)
          ? this.watchLevelAvailable(level, this.currentCountries, this.subDivisions, setting, urlParams)
          : Promise.resolve(),
        (urlParams += buildCountries(this.currentCountries)),
      ])
        .then(async () => {
          urlParams += buildLevel(level)
          await Promise.all([this.initMarkersIndex(), this.initCirclesIndex(), this.initCountiesIndex()])

          if (route.name === 'markersIntention') {
            await this.searchMultipleQueries([
              {
                indexName: this.markersIndex.indexName,
                commitName: 'data/SET_LOCATIONS',
                saveSettings: true,
                commitSettings: 'algolia/UPDATE_SETTINGS_MARKER',
                params: {
                  length: this.nbDisplayMarker,
                  offset: 0,
                  aroundLatLng: this.currentMapCenter,
                  aroundRadius: this.currentMapRadius,
                  ...buildAlgoliaFacets([
                    {
                      key: 'source',
                      values: this.activeFilters,
                    },
                  ]),
                },
              },
              {
                indexName: this.indexCircleOrCountiesByZoom,
                commitName: 'algolia/SET_SOURCES',
                params: {
                  distinct: 1,
                  aroundLatLng: this.currentMapCenter,
                  aroundRadius: this.currentMapRadius,
                  ...buildAlgoliaFacets([
                    {
                      key: 'category',
                      values: [],
                    },
                  ]),
                },
              },
              {
                indexName: this.indexCircleOrCountiesByZoom,
                commitName: 'algolia/SET_CIRCLES_IN_BOX',
                getAllHits: true,
                positionInRequest: 3,
                params: {
                  hitsPerPage: this.nbDisplayCircle,
                  aroundLatLng: this.currentMapCenter,
                  aroundRadius: this.currentMapRadius,
                  ...buildAlgoliaFacets([
                    {
                      key: 'category',
                      values: [],
                    },
                  ]),
                },
              },
            ])
          } else {
            await this.getClientLocations({ setting, urlParams })
          }
        })
        .then(() => {
          this.toggleRefreshData(false)
          this.previousRoute = null
          this.nextRoute = null
        })
    },
    refreshAnalytics() {
      this.toggleRefreshData(true)

      const setting = this.settings.filter(setting => setting.key === this.currentKpi)[0]

      let urlParams = ''
      urlParams += buildSource(this.currentSource)
      urlParams += buildGroups(this.activeGroups)
      urlParams += buildBrands(this.activeBrands, this.brands[this.currentKpi].brands, this.activeGroups)
      urlParams += buildCountries(this.currentCountries)
      urlParams += buildDate(this.currentTimeline)

      return this.getClientAnalytics({ setting, urlParams }).then(() => {
        this.toggleRefreshData(false)
      })
    },
    async watchLevelAvailable(level, currCountries, subDivisions, setting, urlParams) {
      let zones = {}
      zones[setting.key] = []
      for (let c of currCountries) {
        for (let subDivision of subDivisions[setting.key].subdivisions) {
          if (c === subDivision.key) {
            let url = urlParams
            if (parseInt(level) > parseInt(subDivision.max_level)) {
              url += buildCountries([c])
              url += buildLevel(subDivision.max_level)
              await this.getReturnZones({ setting, urlParams: url }).then(zone => {
                zone[setting.key].forEach(element => {
                  zones[setting.key].push(element)
                })
              })
            } else {
              url += buildCountries([c])
              url += buildLevel(level)
              await this.getReturnZones({ setting, urlParams: url }).then(zone => {
                zone[setting.key].forEach(element => {
                  zones[setting.key].push(element)
                })
              })
            }
          }
        }
      }
      this.setZones(zones)
      return Promise.resolve()
    },
    async refreshAlgoliaFilters() {
      await this.search({
        index: this.markersIndex,
        commitName: 'data/SET_LOCATIONS',
        params: {
          length: this.nbDisplayMarker,
          aroundLatLng: this.currentMapCenter,
          aroundRadius: this.currentMapRadius,
          ...buildAlgoliaFacets(
            [
              {
                key: 'source',
                values: this.activeFilters,
              },
            ],
            true
          ),
        },
      })
    },
  },
  beforeRouteUpdate(to, from, next) {
    if (to.name === 'zones' || to.name === 'zonesIntention') {
      this.updateActiveBrands([this.activeBrands[0]])
      this.updateCurrentDistance(0)
    }
    if (to.name === 'location') {
      this.updateActiveBrands(this.brands[this.currentKpi].brands.map(brand => brand.brand.key))
    } else {
      this.previousRoute = from
      this.nextRoute = to
      this.updateCurrentDistance(0)
    }
    this.refreshCountries(false, to)
    next()
  },
}
</script>

<style lang="scss">
.radar {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
}

.maps-info-window {
  text-align: center;
  &__amount {
    font-size: $font-size-title;
    font-weight: 900;
  }

  &__label {
    font-size: $font-size-xs;
  }
}

// close button
button.gm-ui-hover-effect {
  display: none !important;
}

.gm-style .gm-style-iw-d::-webkit-scrollbar-track,
.gm-style .gm-style-iw-d::-webkit-scrollbar-track-piece,
.gm-style .gm-style-iw-c {
  background: rgb(255, 255, 255);
}

.gm-style .gm-style-iw-c {
  padding: $gutter-xs $gutter-sm !important;
  color: $black;
  top: 30px;
}

.gm-style-iw-d {
  overflow: visible !important;
}

.gm-style .gm-style-iw-tc::after,
.gm-style .gm-style-iw-t::after {
  display: none;
}
</style>
